import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { TacticType } from "@front/m19-api-client";
import { AdvertisingStrategyEx, SegmentConfigType, SegmentEx } from "@front/m19-models";
import { SegmentService } from "@front/m19-services";
import {
  IButtonDirective,
  ISelectMenuImports,
  MODAL_DATA,
  ModalOptions,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { switchMap, tap } from "rxjs/operators";
import {
  ProductSegmentModalComponent,
  ProductSegmentModalData,
} from "@m19-board/segments/product-segment-modal.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { map, Observable, of } from "rxjs";
import {
  KeywordSegmentModalComponent,
  KeywordSegmentModalData,
} from "@m19-board/segments/keyword-segment-modal/keyword-segment-modal.component";
import { StrategyService } from "@front/m19-injection-token";

export interface SegmentCreactionRequest {
  tacticType: TacticType;
  segmentType: SegmentConfigType;
}

type TacticTypeOption = SegmentCreactionRequest & {
  label: string;
  disabled: boolean;
  testid: string;
};

const CREATE_NEW_SEGMENT = "create_new_segment" as const;

/**
 * Modal component to create a new tactic
 */
@UntilDestroy()
@Component({
  selector: "app-tactic-add-popup",
  templateUrl: "./tactic-add-popup.component.html",
  imports: [TranslocoDirective, ISelectMenuImports, IButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TacticAddPopupComponent implements OnInit {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "sm" };

  segmentService = inject(SegmentService);
  private translocoService = inject(TranslocoService);

  private readonly modalData = inject<TacticAddPopupModalData>(MODAL_DATA);
  protected readonly modalRef = inject(ModalRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastrService = inject(ToasterService);
  private readonly modalService = inject(ModalService);
  private readonly strategyService = inject(StrategyService);

  strategy = this.modalData.strategy;

  private _allowedSegmentTacticTypes: Set<string> = new Set(
    this.modalData.allowedSegmentTacticTypes.map((s) => s[0] + "_" + s[1]),
  );

  protected readonly CREATE_NEW_SEGMENT = CREATE_NEW_SEGMENT;

  selectedSegment = signal<SegmentEx | typeof CREATE_NEW_SEGMENT | undefined>(undefined);
  creation = computed(() => this.selectedSegment() === CREATE_NEW_SEGMENT);
  tacticType: TacticType = this.modalData.allowedSegmentTacticTypes[0][1];
  segmentType = signal<SegmentConfigType>(this.modalData.allowedSegmentTacticTypes[0][0]);

  readonly TacticType = TacticType;
  readonly SegmentType = SegmentConfigType;

  tacticTypesOptions: TacticTypeOption[] = [];
  selectedTacticType = signal<TacticTypeOption | undefined>(undefined);

  allSegments = signal<SegmentEx[]>([]);
  segmentSearchQuery = signal("");

  filteredSegments = computed(() => {
    const segments = this.allSegments().filter((s) => s.segmentType === this.segmentType() && s.name);
    const query = this.segmentSearchQuery().toLowerCase();
    if (!query) return segments;
    return segments.filter((s) => s.name!.toLowerCase().includes(query));
  });

  isCreatingTactic = signal(false);

  ngOnInit(): void {
    this.segmentService
      .getAllowedSegmentsForStrategy(this.strategy)
      .pipe(untilDestroyed(this))
      .subscribe((segments) => {
        this.allSegments.set(segments);
      });
    this.tacticTypesOptions = [
      {
        label: this.translocoService.translate("tactic-add-popup.keyword_targeting"),
        tacticType: TacticType.LEGACY,
        segmentType: SegmentConfigType.KeywordSegment,
        disabled: !this.supportedSegmentType(TacticType.LEGACY, SegmentConfigType.KeywordSegment),
        testid: `btn-${TacticType.LEGACY}_${SegmentConfigType.KeywordSegment}`,
      },
      {
        label: this.translocoService.translate("tactic-add-popup.product_targeting"),
        tacticType: TacticType.LEGACY,
        segmentType: SegmentConfigType.ProductSegment,
        disabled: !this.supportedSegmentType(TacticType.LEGACY, SegmentConfigType.ProductSegment),
        testid: `btn-${TacticType.LEGACY}_${SegmentConfigType.ProductSegment}`,
      },
      {
        label: this.translocoService.translate("tactic-add-popup.blacklist_keywords"),
        tacticType: TacticType.BLACKLIST,
        segmentType: SegmentConfigType.KeywordSegment,
        disabled: !this.supportedSegmentType(TacticType.BLACKLIST, SegmentConfigType.KeywordSegment),
        testid: `btn-${TacticType.BLACKLIST}_${SegmentConfigType.KeywordSegment}`,
      },
      {
        label: this.translocoService.translate("tactic-add-popup.blacklist_products"),
        tacticType: TacticType.BLACKLIST,
        segmentType: SegmentConfigType.ProductSegment,
        disabled: !this.supportedSegmentType(TacticType.BLACKLIST, SegmentConfigType.ProductSegment),
        testid: `btn-${TacticType.BLACKLIST}_${SegmentConfigType.ProductSegment}`,
      },
    ];

    this.selectedTacticType.set(
      this.tacticTypesOptions.find(
        (o) => !o.disabled && o.tacticType === this.tacticType && o.segmentType === this.segmentType(),
      ),
    );
  }

  supportedSegmentType(tacticType: TacticType, segmentType: SegmentConfigType) {
    return this._allowedSegmentTacticTypes?.has(segmentType + "_" + tacticType);
  }

  selectTacticType(tacticType: TacticTypeOption | undefined) {
    if (tacticType == null) return;
    if (!this.supportedSegmentType(tacticType.tacticType, tacticType.segmentType)) return;
    this.selectedTacticType.set(tacticType);
    this.tacticType = tacticType.tacticType;
    this.segmentType.set(tacticType.segmentType);
    this.selectedSegment.set(undefined);
    this.segmentSearchQuery.set("");
  }

  selectSegment(segment: SegmentEx | typeof CREATE_NEW_SEGMENT | undefined) {
    if (segment == null) return;
    this.selectedSegment.set(segment);
  }

  create(): void {
    this.isCreatingTactic.set(true);
    const selected = this.selectedSegment();
    let segmentId$: Observable<number>;
    if (selected != null && selected !== CREATE_NEW_SEGMENT) {
      segmentId$ = of(selected.segmentId);
    } else {
      segmentId$ = this.createSegment();
    }

    segmentId$.pipe(switchMap((segmentId) => this.addTacticToStrategy(segmentId))).subscribe();
  }

  private createSegment(): Observable<number> {
    this.isCreatingTactic.set(false);
    if (this.segmentType() == SegmentConfigType.ProductSegment) {
      const segmentCreationRef = this.modalService.openModal<ProductSegmentModalData, SegmentEx>(
        ProductSegmentModalComponent,
        {
          modalTitle: `${this.translocoService.translate("common.edit")} ${this.translocoService.translate("product-segment-modal.product_segment")}`,
          data: {
            accountId: this.strategy.accountId,
            marketplace: this.strategy.marketplace,
          },
        },
      );
      return segmentCreationRef.afterClosed().pipe(map((segment) => segment.segmentId));
    } else {
      const segmentCreationModalOption: ModalOptions<KeywordSegmentModalData> = {
        data: {
          accountId: this.strategy.accountId,
          marketplace: this.strategy.marketplace,
        },
        modalTitle: this.translocoService.translate("keyword-segment-modal.create_keyword_segment"),
      };
      return this.modalService
        .openModal<KeywordSegmentModalData, SegmentEx>(KeywordSegmentModalComponent, segmentCreationModalOption)
        .afterClosed()
        .pipe(map((segment) => segment.segmentId));
    }
  }

  private addTacticToStrategy(segmentId: number) {
    return this.strategyService
      .addTacticToStrategy(
        this.strategy.accountId,
        this.strategy.marketplace,
        this.strategy.strategyId,
        segmentId,
        this.tacticType,
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap({
          next: (tactic) => {
            this.toastrService.success(
              this.translocoService.translate("sp-strategy-group-page.tactic_sucessfully_added_to_strategy"),
              this.translocoService.translate("sp-strategy-group-page.tactic_created"),
            );
            this.modalRef.close(tactic);
          },
          error: (error) => {
            this.toastrService.error(
              error,
              this.translocoService.translate("sp-strategy-group-page.tactic_creation_error"),
            );
            this.modalRef.close();
            this.isCreatingTactic.set(false);
          },
        }),
      );
  }
}

export interface TacticAddPopupModalData {
  strategy: AdvertisingStrategyEx;
  allowedSegmentTacticTypes: [SegmentConfigType, TacticType][];
}
